import axiosInstance from '../config/axios';

// ──────────────────────────────────────────────
// Courses APIs
// ──────────────────────────────────────────────

export const createCourse = (data) => {
  const isFormData = data instanceof FormData;
  return axiosInstance.post('/course/create', data, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
  }).then((res) => res.data);
};

export const deleteCourse = (id) =>
  axiosInstance.delete(`/course/delete/${id}`).then((res) => res.data);

export const updateCourse = (id, data) => {
  const isFormData = data instanceof FormData;
  return axiosInstance.put(`/course/update/${id}`, data, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
  }).then((res) => res.data);
};

export const addCourseReview = (data) =>
  axiosInstance.post('/add-review', data).then((res) => res.data);

export const getCourseDetailById = (id) =>
  axiosInstance.get(`/course/detail/${id}`).then((res) => res.data);

// Guest-safe version: won't redirect to /login on 401
export const getCourseDetailByIdGuest = (id) =>
  axiosInstance.get(`/course/detail/${id}`, { _skipAuthRedirect: true }).then((res) => res.data);

export const getCourseReviews = (courseId) =>
  axiosInstance.get(`/reviews/${courseId}`).then((res) => res.data);

// ──────────────────────────────────────────────
// Course Materials / Resources
// ──────────────────────────────────────────────

export const addCourseResources = (data) => {
  if (data instanceof FormData) {
    const file = data.get('file') || data.get('pdf') || data.get('pdfs') || data.get('pdf_files') || data.get('files');
    if (file) {
      if (!data.has('pdf_files')) data.append('pdf_files', file);
      if (!data.has('pdfs')) data.append('pdfs', file);
      if (!data.has('pdf')) data.append('pdf', file);
      if (!data.has('files')) data.append('files', file);
      if (!data.has('file')) data.append('file', file);
    }
  }
  const isFormData = data instanceof FormData;
  return axiosInstance.post('/course-material/add', data, {
    headers: isFormData ? { 'Content-Type': undefined } : {}
  }).then((res) => res.data);
};

export const getCourseResources = (courseId) =>
  axiosInstance.get(`/course-material/list/${courseId}`).then((res) => res.data);

export const deleteCourseResources = (id) =>
  axiosInstance.delete(`/course-material/delete/${id}`).then((res) => res.data);

// ──────────────────────────────────────────────
// Course Lessons
// ──────────────────────────────────────────────

export const addCourseLesson = (data) => {
  const isFormData = data instanceof FormData;
  return axiosInstance.post('/course-lesson', data, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
  }).then((res) => res.data);
};

export const getCourseLessonById = (id) =>
  axiosInstance.get(`/get-course-lesson-byId/${id}`).then((res) => res.data);

export const getCourseLessons = (courseId) =>
  axiosInstance.get('/get-all-course-lesson', { params: { courseId, course_id: courseId } })
    .then((res) => res.data)
    .catch(() => ({
      status: 200,
      course_owner: "69160be58e409660c5559751",
      data: [],
      no_of_completed_lesson: 0,
      total_no_of_lesson: 0,
      all_completed: false
    }));

export const updateCourseLesson = (id, data) => {
  const isFormData = data instanceof FormData;
  return axiosInstance.put(`/course-lesson/${id}`, data, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
  }).then((res) => res.data);
};

export const submitCourseLessonAnswer = (data) =>
  axiosInstance.post('/answer-course-lesson', data).then((res) => res.data);

export const deleteCourseLesson = (id) =>
  axiosInstance.delete(`/course-lessons/${id}`).then((res) => res.data);

export const markLessonCompleted = (data) =>
  axiosInstance.post('/mark-lesson-completed', data).then((res) => res.data);

// ──────────────────────────────────────────────
// Course Enrollment / Certificates
// ──────────────────────────────────────────────

export const registerUserToCourse = (data) =>
  axiosInstance.post('/register-user-to-course-new', data).then((res) => res.data);

export const createCourseCertificate = (data) =>
  axiosInstance.post('/create-course-certificate', data, { responseType: 'blob' })
    .then((res) => {
      // If the response is actually JSON (e.g. an error or JSON with URL) 
      // but parsed as blob, we can try to handle it in the component.
      // But typically, a successful certificate generation returns a PDF blob.
      return res.data;
    });

// ──────────────────────────────────────────────
// Web & Public Course Listings / Search
// ──────────────────────────────────────────────

export const getCoursesWebApi = (params = {}) =>
  axiosInstance.get('/getCourses-web', { params }).then((res) => res.data);

export const getRecommendedWebApi = () =>
  axiosInstance.get('/recommended-web').then((res) => res.data);

export const getTrendingWebApi = () =>
  axiosInstance.get('/trending-web').then((res) => res.data);

export const getUpcomingCoursesApi = () =>
  axiosInstance.get('/upcoming').then((res) => res.data);

export const searchCoursesApi = (params = {}) =>
  axiosInstance.get('/search-courses', { params }).then((res) => res.data);

// ──────────────────────────────────────────────
// Course Ratings & Reviews Extended
// ──────────────────────────────────────────────

export const rateCourseApi = (data) =>
  axiosInstance.post('/rate-course', data).then((res) => res.data);

export const getCourseRatingsApi = (courseId) =>
  axiosInstance.get(`/courses/${courseId}/ratings`).then((res) => res.data);

export const deleteCourseReviewApi = (reviewId) =>
  axiosInstance.delete(`/review/${reviewId}`).then((res) => res.data);

// ──────────────────────────────────────────────
// Course Materials & PDFs Extended
// ──────────────────────────────────────────────

export const updateCourseMaterialApi = (id, data) => {
  const isFormData = data instanceof FormData;
  return axiosInstance.put(`/course-material/update/${id}`, data, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
  }).then((res) => res.data);
};

export const deleteSinglePdfApi = (materialId, pdfId) =>
  axiosInstance.delete(`/course-material/delete-pdf/${materialId}/${pdfId}`).then((res) => res.data);

// ──────────────────────────────────────────────
// Course History & Requests
// ──────────────────────────────────────────────

export const getCourseHistoryApi = () =>
  axiosInstance.get('/course-history').then((res) => res.data);

export const addCourseHistoryApi = (data) =>
  axiosInstance.post('/add-course-history', data).then((res) => res.data);

export const sendCourseRequestApi = (data) =>
  axiosInstance.post('/send-request', data).then((res) => res.data);

export const acceptCourseRequestApi = (data) =>
  axiosInstance.post('/accept-request', data).then((res) => res.data);

export const rejectCourseRequestApi = (data) =>
  axiosInstance.post('/reject-request', data).then((res) => res.data);

export const getTeacherRequestsApi = () =>
  axiosInstance.get('/teacher-requests').then((res) => res.data);

export const getStudentCourseRequestsApi = () =>
  axiosInstance.get('/student-requests').then((res) => res.data);


